import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import marketingApi from "../../api/marketingApi";
import { getCachedBanners } from "../../marketing/bannerCache";

export default function BannerSlot({ placement, variant }) {
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const dims = useMemo(() => {
    return variant === "header"
      ? { height: 90, maxWidth: 728 }
      : { height: 250, maxWidth: 300 };
  }, [variant]);

  // Fetch + clean
  useEffect(() => {
    let isMounted = true;

    async function fetchBanners() {
      setLoading(true);
      try {
        const list = await getCachedBanners({
          placement,
          fetcher: marketingApi.getActiveBanners,
          ttlMs: 5 * 60 * 1000,
        });

        const cleaned = (Array.isArray(list) ? list : []).filter(
          (b) => b?.image_url && b?.target_url
        );

        if (isMounted) {
          setBanners(cleaned);
          setIdx(0);
        }
      } catch {
        if (isMounted) {
          setBanners([]);
          setIdx(0);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchBanners();
    return () => {
      isMounted = false;
    };
  }, [placement]);

  // Rotation timer (safe cleanup)
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!banners || banners.length <= 1) return;

    timerRef.current = setInterval(() => {
      setIdx((prev) => (prev + 1) % banners.length);
    }, 7000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [banners]);

  // Keep idx in range
  useEffect(() => {
    if (idx >= (banners?.length ?? 0)) setIdx(0);
  }, [idx, banners]);

  // Optional: preload all once when the list arrives (reduces per-tick Network noise)
  useEffect(() => {
    if (!banners?.length) return;
    for (const b of banners) {
      const img = new Image();
      img.src = b.image_url;
    }
  }, [banners]);

  const current = banners?.[idx];
  const currentSrc = current?.image_url || "";
  const currentHref = current?.target_url || "";

  const hasBanner = !!currentSrc && !!currentHref && !loading && banners.length > 0;

  if (!hasBanner) {
    return (
      <Box
        sx={{
          width: "100%",
          height: dims.height,
          maxWidth: dims.maxWidth,
          mx: "auto",
          bgcolor: "grey.200",
          border: "1px solid",
          borderColor: "grey.300",
          borderRadius: 2,
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: dims.height,
        maxWidth: dims.maxWidth,
        mx: "auto",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        component="a"
        href={currentHref}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ width: "100%", height: "100%", display: "block" }}
      >
        <Box
          component="img"
          key={currentSrc} // forces remount on src change (good)
          src={currentSrc}
          alt={`${placement} banner`}
          onError={() => {
            if (banners.length > 1) {
              setIdx((prev) => (prev + 1) % banners.length);
            }
          }}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      </Box>
    </Box>
  );
}